import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import * as api from '../services/api';

const FriendsContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useFriends = () => {
  const context = useContext(FriendsContext);
  if (!context) {
    throw new Error('useFriends must be used within a FriendsProvider');
  }
  return context;
};

export const FriendsProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountFriendsProvider key={user?.id ?? 'guest'} user={user}>{children}</AccountFriendsProvider>;
};

const AccountFriendsProvider = ({ children, user }) => {
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Загрузить все данные о друзьях
  const loadFriendsData = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [friendsData, incomingData, outgoingData, suggestionsData, followersData, followingData] = await Promise.all([
        api.getFriends(),
        api.getIncomingRequests(),
        api.getOutgoingRequests(),
        api.getFriendSuggestions(),
        api.getFollowers(),
        api.getFollowing()
      ]);
      
      setFriends(friendsData);
      setIncomingRequests(incomingData);
      setOutgoingRequests(outgoingData);
      setSuggestions(suggestionsData);
      setFollowers(followersData);
      setFollowing(followingData);
    } catch (err) {
      console.error('Ошибка загрузки данных друзей:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Загружать данные при монтировании и при изменении пользователя
  useEffect(() => {
    loadFriendsData();
  }, [loadFriendsData]);

  // Получить список друзей
  const getFriends = () => friends;

  // Получить входящие заявки
  const getIncomingRequests = () => incomingRequests;

  // Получить исходящие заявки
  const getOutgoingRequests = () => outgoingRequests;

  // Получить рекомендации
  const getSuggestions = () => suggestions;

  // Проверить, является ли пользователь другом
  const isFriend = (userId) => {
    return friends.some(friend => friend.id === userId);
  };

  // Проверить, есть ли входящая заявка от пользователя
  const hasIncomingRequest = (userId) => {
    return incomingRequests.some(request => request.id === userId);
  };

  // Проверить, есть ли исходящая заявка пользователю
  const hasOutgoingRequest = (userId) => {
    return outgoingRequests.some(request => request.id === userId);
  };

  // Отправить заявку в друзья
  const sendFriendRequest = async (userId) => {
    try {
      await api.sendFriendRequest(userId);
      // Обновляем списки
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка отправки заявки:', err);
      throw err;
    }
  };

  // Отменить заявку
  const cancelFriendRequest = async (userId) => {
    try {
      await api.cancelFriendRequest(userId);
      // Обновляем списки
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка отмены заявки:', err);
      throw err;
    }
  };

  // Принять заявку в друзья
  const acceptFriendRequest = async (userId) => {
    try {
      await api.acceptFriendRequest(userId);
      // Обновляем списки
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка принятия заявки:', err);
      throw err;
    }
  };

  // Отклонить заявку в друзья
  const rejectFriendRequest = async (userId) => {
    try {
      await api.rejectFriendRequest(userId);
      // Обновляем списки
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка отклонения заявки:', err);
      throw err;
    }
  };

  // Удалить друга
  const removeFriend = async (userId) => {
    try {
      await api.removeFriend(userId);
      // Обновляем списки
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка удаления друга:', err);
      throw err;
    }
  };

  // Поиск пользователей
  const searchUsers = async (query) => {
    if (!query.trim()) return [];
    
    try {
      const results = await api.searchUsersForFriends(query);
      return results;
    } catch (err) {
      console.error('Ошибка поиска пользователей:', err);
      return [];
    }
  };

  // Получить общих друзей
  const getMutualFriends = async (userId) => {
    try {
      const mutualFriends = await api.getMutualFriends(userId);
      return mutualFriends;
    } catch (err) {
      console.error('Ошибка получения общих друзей:', err);
      return [];
    }
  };

  // Подсчитать количество общих друзей
  const getMutualFriendsCount = async (userId) => {
    try {
      const mutualFriends = await api.getMutualFriends(userId);
      return mutualFriends.length;
    } catch (err) {
      console.error('Ошибка подсчета общих друзей:', err);
      return 0;
    }
  };

  // ─── Подписки ─────────────────────────────────────────────────────────

  // Проверить, подписан ли я на пользователя
  const isSubscribed = (userId) => {
    return following.some(f => f.id === userId);
  };

  // Получить подписчиков
  const getFollowers = () => followers;

  // Получить подписки (на кого я подписан)
  const getFollowing = () => following;

  // Подписаться
  const subscribe = async (userId) => {
    try {
      await api.subscribeToUser(userId);
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка подписки:', err);
      throw err;
    }
  };

  // Отписаться
  const unsubscribe = async (userId) => {
    try {
      await api.unsubscribeFromUser(userId);
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка отписки:', err);
      throw err;
    }
  };

  // Оставить в подписчиках (отклонить заявку, но сохранить как подписку)
  const keepAsFollower = async (userId) => {
    try {
      await api.keepAsFollower(userId);
      await loadFriendsData();
    } catch (err) {
      console.error('Ошибка сохранения подписчика:', err);
      throw err;
    }
  };

  const value = {
    friends,
    incomingRequests,
    outgoingRequests,
    suggestions,
    followers,
    following,
    loading,
    error,
    getFriends,
    getIncomingRequests,
    getOutgoingRequests,
    getSuggestions,
    getFollowers,
    getFollowing,
    isFriend,
    isSubscribed,
    hasIncomingRequest,
    hasOutgoingRequest,
    sendFriendRequest,
    cancelFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    subscribe,
    unsubscribe,
    keepAsFollower,
    searchUsers,
    getMutualFriends,
    getMutualFriendsCount,
    refreshFriends: loadFriendsData
  };

  return (
    <FriendsContext.Provider value={value}>
      {children}
    </FriendsContext.Provider>
  );
};

export default FriendsContext;